import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Text,
} from '@fluentui/react-components';
import { MessageBarType } from '@fluentui/react';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import { connector, Props, useStyles } from './QueueDisplayRegistration.Functions';
import { QueueDisplayScreen } from '../../../../models/Entities/Turnero';

const QueueDisplayRegistrationComponent: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultSectorId,
  defaultSedeId,
  list,
  isSaving,
  successAddingNewOne,
  failOnAddingNewOne,
  error,
  CreateQueueDisplayScreen,
  GetQueueDisplayScreenById,
  GetQueueDisplayScreens,
}) => {
  const styles = useStyles();

  const formatRegistrationInput = (value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 8);
    if (digitsOnly.length <= 4) return digitsOnly;
    return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4)}`;
  };

  const [registrationCode, setRegistrationCode] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [pollingId, setPollingId] = useState<number | undefined>(undefined);
  const [allowCancel, setAllowCancel] = useState(false);

  const pollInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isWorking = useMemo(() => isSaving || pollingId !== undefined, [isSaving, pollingId]);

  const cleanupTimers = () => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
    if (cancelTimeout.current) {
      clearTimeout(cancelTimeout.current);
      cancelTimeout.current = null;
    }
  };

  const resetState = () => {
    cleanupTimers();
    setRegistrationCode('');
    setValidationErrors([]);
    setSubmitted(false);
    setPollingId(undefined);
    setAllowCancel(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !submitted) return;

    if (successAddingNewOne && !pollingId) {
      const match = (list || []).find((item: QueueDisplayScreen) => item.registrationNumber === registrationCode.trim());
      if (match?.id) {
        setPollingId(match.id);
        if (!pollInterval.current) {
          pollInterval.current = setInterval(() => GetQueueDisplayScreenById(match.id!), 5000);
        }
        if (!cancelTimeout.current) {
          cancelTimeout.current = setTimeout(() => setAllowCancel(true), 15000);
        }
      }
    }
  }, [successAddingNewOne, isOpen, submitted, list, registrationCode, pollingId, GetQueueDisplayScreenById]);

  useEffect(() => {
    if (!isOpen || !pollingId) return;
    const found = (list || []).find((item) => item.id === pollingId);
    if (found?.fingerPrint || found?.fingerprint) {
      cleanupTimers();
      setAllowCancel(true);
      setPollingId(undefined);
      GetQueueDisplayScreens();
      onClose();
    }
  }, [list, pollingId, isOpen, GetQueueDisplayScreens, onClose]);

  useEffect(() => {
    if (failOnAddingNewOne && submitted) {
      setAllowCancel(true);
    }
  }, [failOnAddingNewOne, submitted]);

  useEffect(() => {
    return () => {
      cleanupTimers();
    };
  }, []);

  const validate = () => {
    const errors: string[] = [];
    const trimmed = registrationCode.trim();
    if (!trimmed) {
      errors.push('Debe ingresar un código de registro');
    } else if (!/^\d{4}-\d{4}$/.test(trimmed)) {
      errors.push('El formato del código debe ser 9999-9999');
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleRegister = () => {
    setSubmitted(true);
    if (!validate()) return;

    cleanupTimers();
    setAllowCancel(false);
    setPollingId(undefined);
    cancelTimeout.current = setTimeout(() => setAllowCancel(true), 15000);

    const payload: QueueDisplayScreen = {
      registrationNumber: registrationCode.trim(),
      sectorId: defaultSectorId ?? null,
      sedeId: defaultSedeId ?? null,
      isActive: true,
    };

    CreateQueueDisplayScreen(payload);
  };

  const handleCancel = () => {
    cleanupTimers();
    onClose();
  };

  return (
    <Dialog modalType="alert" open={isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Registrar display</DialogTitle>
          <DialogContent>
            <div className={styles.container}>
              {validationErrors.length > 0 && (
                <MessageComponent
                  message="Verifique la información suministrada"
                  type={MessageBarType.error}
                  itemList={validationErrors}
                />
              )}
              {failOnAddingNewOne && (
                <MessageComponent
                  message={error?.ErrorMessage || 'Ocurrió un error al registrar'}
                  type={MessageBarType.error}
                  itemList={error?.Errors}
                />
              )}
              <Field label="Código de registro" required>
                <Input
                  size='large'
                  value={registrationCode}
                  onChange={(_, data) => setRegistrationCode(formatRegistrationInput(data.value))}
                  disabled={isWorking}
                  placeholder="9999-9999"
                />
              </Field>
              {isWorking && (
                <div className={styles.statusRow}>
                  <Spinner size="small" />
                  <Text>Registrando...</Text>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel} disabled={!allowCancel && isWorking}>
              Cancelar
            </Button>
            <Button appearance="primary" onClick={handleRegister} disabled={isWorking}>
              Registrar
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default connector(QueueDisplayRegistrationComponent);
